<template>
  <Modal
    :open="show"
    @closed="$emit('close')"
    dialog-size="modal-lg"
  >
    <div class="modal-header border-0">
      <h5 class="modal-title m-0 d-flex align-items-center">
        Leave Request Details
      </h5>
      <button @click="$emit('close')" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
      </button>
    </div>
    <div class="modal-body">
      <div v-if="request">
        <div class="mb-3">
          <h5 class="mb-3">Employee Information</h5>
          <div class="row">
            <div class="col-md-6 mb-2">
              <strong>Name:</strong> {{ request.employee?.first_name }} {{ request.employee?.last_name }}
            </div>
            <div class="col-md-6 mb-2">
              <strong>Employee No:</strong> {{ request.employee?.employee_no || 'N/A' }}
            </div>
            <div class="col-md-6 mb-2">
              <strong>Department:</strong> {{ request.employee?.department?.name || 'N/A' }}
            </div>
            <div class="col-md-6 mb-2">
              <strong>Designation:</strong> {{ request.employee?.designation?.name || 'N/A' }}
            </div>
          </div>
        </div>

        <hr>

        <div class="mb-3">
          <h5 class="mb-3">Leave Information</h5>
          <div class="row">
            <div class="col-md-6 mb-2">
              <strong>Leave Type:</strong> {{ request.leave_type?.name || 'N/A' }}
            </div>
            <div class="col-md-6 mb-2">
              <strong>Status: </strong>
              <span class="badge" :class="getStatusBadgeClass(request.status)">
                {{ capitalizeFirstLetter(request.status) }}
              </span>
            </div>
            <div class="col-md-6 mb-2">
              <strong>Start Date:</strong> {{ formatDate(request.start_date) }}
            </div>
            <div class="col-md-6 mb-2">
              <strong>End Date:</strong> {{ formatDate(request.end_date) }}
            </div>
            <div class="col-md-6 mb-2">
              <strong>Days:</strong> {{ request.days }}
            </div>
            <div class="col-md-6 mb-2">
              <strong>Applied On:</strong> {{ formatDate(request.created_at) }}
            </div>
            <div class="col-12 mb-2">
              <strong>Reason:</strong> {{ request.reason || 'N/A' }}
            </div>
            <div class="col-12 mb-2" v-if="request.files?.length">
              <strong>Attachments: </strong>
              <a
                v-for="file in request.files"
                :key="file.id"
                class="btn btn-sm btn-outline-success me-1"
                :href="getAttachmentUrl(file)"
                target="_blank">View Attachment</a>
            </div>
          </div>
        </div>

        <hr>

        <div v-if="request.approvals?.length > 0" class="mb-3">
          <h5 class="mb-3">Approval Information</h5>
          <div class="table-responsive">
            <table class="table table-bordered">
              <thead class="table-light">
                <tr>
                  <th>Approver</th>
                  <th>Status</th>
                  <th>Comments</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="approval in request.approvals" :key="approval.id">
                  <td>{{ approval.user?.first_name }} {{ approval.user?.last_name }}</td>
                  <td>
                    <span class="badge" :class="getStatusBadgeClass(approval.status)">
                      {{ capitalizeFirstLetter(approval.status) }}
                    </span>
                  </td>
                  <td>{{ approval.comments || 'N/A' }}</td>
                  <td>{{ approval.approved_at ? formatDate(approval.approved_at) : 'N/A' }}</td>
                  <td>
                    <button
                      v-if="approval.status === 'pending' && isPrimary"
                      type="button"
                      class="btn btn-sm btn-outline-success"
                      @click="openReassignModal(approval.id)">
                      Reassign
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="d-flex justify-content-end mt-4">
          <button type="button" class="btn btn-outline-secondary" @click="$emit('close')">Close</button>
        </div>
      </div>
    </div>
  </Modal>

  <!-- Reassign Leave Request Modal -->
  <ReassignLeaveRequestModal
    :show="showReassignModal"
    @close="closeReassignModal"
    @reassign="handleReassign"
  />
</template>

<script>
import formatDate from '@/utils/formatDate';
import {decrypt} from "@/utils/crypto";
import ReassignLeaveRequestModal from '@/components/leave/ReassignLeaveRequestModal.vue';

export default {
  name: 'ViewLeaveRequestModal',
  props: {
    show: {
      type: Boolean,
      required: true
    },
    request: {
      type: Object,
      default: () => ({})
    },
  },
  components: {
    ReassignLeaveRequestModal,
  },
  data() {
    return {
      baseUrl: process.env.VUE_APP_API_URL || '',
      profile: {},
      showReassignModal: false,
      selectedApprovalId: null,
    };
  },

  methods: {
    formatDate,
    capitalizeFirstLetter(string) {
      if (!string) return '';
      return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
    },
    getStatusBadgeClass(status) {
      const statusMap = {
        'approved': 'bg-success',
        'rejected': 'bg-danger',
        'cancelled': 'bg-secondary',
        'pending': 'bg-warning',
        'draft': 'bg-info'
      };
      return statusMap[status?.toLowerCase()] || 'bg-secondary';
    },
    getAttachmentUrl(file) {
      if (file.url) return file.url;
      if (file.id && this.baseUrl) {
        return `${this.baseUrl}/file/${file.id}`;
      }
      return '#';
    },
    openReassignModal(approvalId) {
      this.selectedApprovalId = approvalId;
      this.showReassignModal = true;
    },
    closeReassignModal() {
      this.showReassignModal = false;
    },
    handleReassign(userId) {
      this.$emit('reassign', { approvalId: this.selectedApprovalId, userId });
      this.showReassignModal = false;
    },
  },

  created() {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
  },

  computed: {
    isPrimary() {
      return this.profile.role === 'super-admin' || this.profile.role === 'primary';
    }
  }
};
</script>
